import { useEffect, startTransition } from 'react'
import { getFirAuth } from '../../Api/firebase/firebaseConnect'
import { onAuthStateChanged } from 'firebase/auth'
import {
  handleAuthError,
  handleFirebaseTransactionError,
  normalizeError,
  resetFirebasePersistence,
} from '../../Utils'
import { ANALYTICS_TAGS } from '../../Constant'
import { NO_USER } from '../../Api/firebase/AuthData'
import type { AuthOperations, GoogleAuthOperations, AuthStateSetters } from './types'

type UseFirebaseAuthListenerParams = {
  authOperations: AuthOperations
  googleAuth: GoogleAuthOperations
  authStateSetters: AuthStateSetters
  sendMasterBrazeEvents: (params: { isLoggedIn: boolean }) => void
  showFirebaseErrorSnackbar: (refreshFn: () => void) => void
}

/**
 * Manages the Firebase `onAuthStateChanged` listener.
 * This hook is responsible for reacting to user sign-in and sign-out events.
 */
export const useFirebaseAuthListener = ({
  authOperations,
  googleAuth,
  authStateSetters,
  sendMasterBrazeEvents,
  showFirebaseErrorSnackbar,
}: UseFirebaseAuthListenerParams) => {
  const { setUser, setAuthLoading, setUserFavorites } = authStateSetters

  useEffect(() => {
    let unsubscribe: (() => void) | null = null

    const authListener = async () => {
      const auth = await getFirAuth()
      if (!auth) return

      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        try {
          if (firebaseUser && !firebaseUser.isAnonymous) {
            setAuthLoading(true)
            await authOperations.handleUserChange()
            sendMasterBrazeEvents({ isLoggedIn: true })
          } else {
            googleAuth.handleGoogleOneTapPrompt(NO_USER)
            startTransition(() => {
              setUser(NO_USER)
              setUserFavorites({
                topics: [],
                favPlayers: [],
                favTeam: null,
              })
              setAuthLoading(false)
            })
            sendMasterBrazeEvents({ isLoggedIn: false })
          }
        } catch (error) {
          const errorMessage = normalizeError(error)
          if (
            handleFirebaseTransactionError(errorMessage.message, 'auth_state_change')
          ) {
            const refreshFn = await resetFirebasePersistence()
            showFirebaseErrorSnackbar(refreshFn)
            return
          }

          handleAuthError(
            ANALYTICS_TAGS.auth_state_change_error,
            error,
            'auth_state_change',
            { hasUser: !!firebaseUser }
          )
          startTransition(() => {
            setAuthLoading(false)
            setUser(NO_USER)
          })
        }
      })
    }

    authListener()

    return () => {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [
    authOperations,
    googleAuth,
    authStateSetters,
    sendMasterBrazeEvents,
    showFirebaseErrorSnackbar,
  ])
}
